import { useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { monthKey } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
const num = (v) => Number(String(v ?? "").replace(/\s/g, "").replace(",", ".")) || 0;

export function parseCsv(text, year) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.trim().startsWith("#"));
  if (lines.length < 2) return [];
  const delim = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ";" : ",";
  const headers = lines[0].split(delim).map(norm);
  const idx = (names) => headers.findIndex((h) => names.includes(h));
  const iNom = idx(["nom", "name", "salarie"]);
  const iPrenom = idx(["prenom", "firstname"]);
  const iPoste = idx(["poste", "emploi", "fonction"]);
  const iApprenti = idx(["apprenti", "apprentice"]);
  const iMensuel = idx(["brutmensuel", "salairemensuel", "mensuel"]);
  const iAnnuel = idx(["brutannuel", "salaireannuel", "annuel", "remuneration", "brut"]);
  const monthIdx = Array.from({ length: 12 }, (_, i) => headers.findIndex((h) => h === `m${i + 1}` || h === String(i + 1).padStart(2, "0") || h === `mois${i + 1}`));
  return lines.slice(1).map((line) => {
    const cols = line.split(delim);
    const remunerations = {};
    if (monthIdx.some((i) => i >= 0)) {
      monthIdx.forEach((i, m) => { if (i >= 0) remunerations[monthKey(year, m + 1)] = num(cols[i]); });
    } else {
      const monthly = iMensuel >= 0 ? num(cols[iMensuel]) : iAnnuel >= 0 ? num(cols[iAnnuel]) / 12 : 0;
      for (let m = 1; m <= 12; m++) remunerations[monthKey(year, m)] = Math.round(monthly * 100) / 100;
    }
    return { nom: (cols[iNom] || "").trim(), prenom: iPrenom >= 0 ? (cols[iPrenom] || "").trim() : "", poste: iPoste >= 0 ? (cols[iPoste] || "").trim() : "", apprenti: iApprenti >= 0 && ["oui", "1", "true", "x", "o"].includes((cols[iApprenti] || "").trim().toLowerCase()), remunerations };
  }).filter((r) => r.nom && r.nom.toUpperCase() !== "TOTAL");
}

export const CsvImportDialog = ({ open, onClose, companyId, year, onImported }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    file.text().then((t) => {
      const parsed = parseCsv(t, year);
      setRows(parsed);
      if (!parsed.length) toast.error("Aucune ligne reconnue. Vérifiez les en-têtes (nom;prenom;poste;brut_mensuel).");
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/employees/import`, { rows });
      toast.success(`${data.imported} salarié(s) importé(s)`);
      onImported();
      onClose();
      setRows([]); setFileName("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Importer des salariés (CSV)</DialogTitle>
          <DialogDescription>Colonnes acceptées : <code>nom;prenom;poste;apprenti(oui/non);brut_mensuel</code> ou <code>brut_annuel</code>, ou colonnes <code>m1…m12</code>. Séparateur « ; » ou « , ». Les montants seront affectés à l'exercice {year}.</DialogDescription>
        </DialogHeader>
        <label
          className="border-2 border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-brand-500 hover:bg-brand-50 transition-colors block"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
          data-testid="csv-import-dropzone"
        >
          <Upload className="h-6 w-6 mx-auto text-muted-foreground/70 mb-2" />
          <div className="text-sm text-muted-foreground">{fileName || "Glissez un fichier CSV ici ou cliquez pour sélectionner"}</div>
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="csv-import-file-input" />
        </label>
        {rows.length > 0 && (
          <div className="text-sm text-foreground/80" data-testid="csv-import-preview">
            <span className="font-semibold">{rows.length}</span> ligne(s) prête(s) — ex. {rows[0].nom} {rows[0].prenom} : {Object.values(rows[0].remunerations).reduce((a, b) => a + b, 0).toLocaleString("fr-FR")} € / an
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={!rows.length || busy} data-testid="csv-import-submit">{busy ? "Import…" : "Importer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
